/**
 * Split, eject, fire, say and switch slots — from a key or a thumb to the wire. The coalescing
 * half of it, pure and DOM-free so it can be tested without a canvas or a socket.
 *
 * All five are *edges* on the ordinary input frame (`clientInputSchema.split` / `.eject` /
 * `.usePowerUp` / `.saying` / `.selectSlot`): one frame carrying one is one press, spent by the
 * next tick, and a re-sent one is a second split. That single sentence is where every rule in
 * this file comes from:
 *
 * - **A press is queued, never sent.** `GameConnection` puts at most one input frame on the wire
 *   per TICK_MS, because the simulation accepts MAX_INPUTS_PER_TICK (1) of them and drops the
 *   rest. A triple-tap inside one tick therefore has to become three frames on three consecutive
 *   ticks rather than one frame or three dropped ones — which is exactly the 2 -> 4 -> 8 cascade
 *   the split card is about. `ActionQueue` is that buffer.
 * - **Nothing is debounced.** The only bound is ACTION_QUEUE_MAX, and it is a backlog cap rather
 *   than a rate limit: it stops a key held through a stalled socket owing a hundred splits when
 *   the socket comes back, and it is deliberately deeper than the cascade needs.
 * - **A held control repeats on the tick clock.** `HoldRepeat` turns "still down" into presses,
 *   after a delay long enough that a single tap is a single press.
 * - **A double tap is a press too**, and `DoubleTap` is the whole of that: two downs close in
 *   time *and* in space. The slop is what separates a double-click from two deliberate clicks
 *   at opposite ends of the arena, which on a steering surface is a distinction that matters.
 * - **Two of the five are a CHOICE rather than a QUANTITY**, and they are the two that carry a
 *   value: a saying and a slot. Three presses of either are one answer, not three, so each holds
 *   one pending value with the latest winning rather than a counter — see `queuedSaying` and
 *   `queuedSlot` for the two halves of why.
 * - **AND ONE OF THE SIX IS NOT SPENT BY A FRAME AT ALL.** A vote answer is a choice like those
 *   two — one slot, latest wins — and it is also the only value here with no acknowledgement
 *   anywhere on the protocol, because an open vote deliberately reports nothing about who has
 *   answered. So `take()` leaves it alone and `flushInput` re-states it on every frame until the
 *   vote closes; `clearVote` is what ends that. See `heldVote`.
 *
 * ── AND ONE CONTROL IN THIS FILE IS NOT AN EDGE AT ALL ──
 *
 * **THE SPRINT IS A LEVEL, AND IT IS THE FIRST CONTROL IN THIS CLIENT THAT IS NOT AN EDGE.**
 * Everything above is a press that happened once and has to be delivered once; a sprint is the
 * answer to "is the control down *right now*", read on every `flushInput` exactly as the heading
 * is, and `clientInputSchema.sprint` says out loud that it breaks the rule every other action on
 * that frame follows. It therefore does not go near `ActionQueue`, which counts and spends, and
 * it must never go near `HoldRepeat`, which exists to turn a held control into repeated *edges* —
 * that is the right shape for a split and precisely the wrong one here, where holding the control
 * is one continuous instruction rather than twenty presses a second.
 *
 * What lives here is `SprintControl`, which is the button's half of that level; the keyboard's
 * half is `ARENA_KEY_BINDINGS.sprint` read per frame in `ArenaScene`, which ORs the two and hands
 * `GameConnection.setSprint` one answer. See `SprintControl` for why the two halves are folded
 * there rather than in the connection.
 */

import { type MassPoint, massToRadius, TICK_MS } from '@chompdome/shared/constants';

/**
 * Which edge action a press is.
 *
 * A union rather than three parallel members everywhere downstream, so the queue, the hold
 * repeat, the scene's key table and the cue drawing are each written once — which is what makes
 * "the power-up is a third one of these" a list entry rather than a third code path.
 */
export type EdgeAction = 'split' | 'eject' | 'powerUp';

/** Every edge action, in the order the controls are laid out. */
export const EDGE_ACTIONS = ['split', 'eject', 'powerUp'] as const;

/**
 * The most presses of one action that may be owed at once.
 *
 * Four rather than three: three consecutive frames are what take a cluster 2 -> 4 -> 8, and a
 * cap *at* the cascade would be a cap the cascade could hit. Past it a press is dropped, which
 * is the honest outcome — the alternative is a queue that keeps firing seconds after the player
 * stopped asking.
 */
export const ACTION_QUEUE_MAX = 4;

/** What one input frame may carry. All at once is legal; the simulation runs every phase. */
export interface QueuedActions {
  split?: true;
  eject?: true;
  usePowerUp?: true;
  /**
   * The canned saying picked since the last frame, as an **id** — never text. That is the whole
   * security design of the feature (`packages/shared/src/constants/sayings.ts`): the server
   * resolves the words, so there is no path by which this client can put a string of its own
   * choosing over somebody else's blob.
   */
  saying?: string;
  /**
   * Which slot of our own inventory `usePowerUp` should point at from now on, as an **index** —
   * never a kind. `clientInputSchema.selectSlot`'s rule, and it is `usePowerUp` naming nothing at
   * all one step along: a client that could name the power-up to select could select one it never
   * picked up.
   */
  selectSlot?: number;
  /**
   * Ask the room to end a round no human is still alive in.
   *
   * A `true`-or-absent edge like the three above it rather than a level like the sprint: one
   * frame carrying it is one request, and the round ending is the acknowledgement. It names
   * nothing, `usePowerUp`'s rule. See `clientInputSchema.endRound`.
   */
  endRound?: true;
}

/**
 * Presses waiting for an input frame to ride out on.
 *
 * Counters rather than a list: two queued splits are indistinguishable from each other, and the
 * only question `flushInput` ever asks is "is one owed".
 */
export class ActionQueue {
  /** One counter per action, keyed by the action itself so a fourth needs no new field. */
  private readonly owed: Record<EdgeAction, number> = { split: 0, eject: 0, powerUp: 0 };

  /**
   * The saying waiting for a frame, or null — **one slot, latest wins**, which is where a saying
   * parts company with the three edge actions above.
   *
   * A split is a *quantity*: three presses are three doublings, so they are counted and spread
   * over three frames. A saying is a *choice*, and the server refuses a second one inside
   * SAYING_COOLDOWN_TICKS anyway — so a queue of them would be a queue of presses the room is
   * going to throw away, fired seconds after the player picked something else. Replacing the
   * slot is the honest reading of "actually, that one".
   */
  private queuedSaying: string | null = null;

  /**
   * The slot waiting for a frame, or null — **one slot, latest wins**, exactly as a saying is and
   * for the same reason turned one notch harder.
   *
   * A split is a *quantity*, so three presses are three doublings; a switch is a *position*, and
   * three presses of a cycle key are one destination. Queueing them would spend three input
   * frames arriving at a slot the player asked for once — and, worse, the two frames in between
   * would each pass through a slot they did not want to be pointing at, which on a timed power-up
   * is the difference between keeping a window and destroying it.
   */
  private queuedSlot: number | null = null;

  /**
   * The vote answer being re-sent, or null — **one slot, latest wins, and NOT spent by a frame**.
   *
   * A saying and a slot are choices a frame spends, because the room answers each of them in a
   * way this client can read: a bubble comes back, and the inventory's own `selected` moves. An
   * open vote answers nothing at all — `serverVoteSchema` carries no tally, no count and no list
   * of who has answered, on purpose, because a running count is a running instruction to join
   * whichever side is winning — so there is no acknowledgement for this one to wait for and a
   * single frame lost to the simulation's one-input-per-tick cap would be a vote nobody counted.
   *
   * It is therefore *held*, exactly as a duel's answer is held in `GameConnection`, and re-stated
   * on every input frame until `clearVote`. That is bounded by construction: VOTE_DURATION_SECONDS
   * of one small frame a tick, once per vote, and the room refuses every answer after the first
   * in silence — which is exactly what makes re-sending safe.
   *
   * Latest wins for the saying's reason and it is nearly unreachable for the connection's: a
   * second answer is refused there before it ever gets here, because the first one is the one
   * that counts.
   */
  private heldVote: string | null = null;

  /** Whether a request to end the round is waiting for a frame. See `endRound`. */
  private queuedEndRound = false;

  press(action: EdgeAction): void {
    this.owed[action] = Math.min(this.owed[action] + 1, ACTION_QUEUE_MAX);
  }

  /** Pick a saying to raise. The id is resolved to words by the server and by nothing here. */
  say(sayingId: string): void {
    this.queuedSaying = sayingId;
  }

  /**
   * Point at a slot. An index into the inventory the *server* last sent, never a power-up's kind.
   *
   * Nothing here checks the index against anything, deliberately: what a player is holding is the
   * server's record, the schema bounds the field at POWER_UP_INVENTORY_SIZE and the simulation
   * refuses an index that names no slot in silence. A queue that second-guessed it would be a
   * second opinion about the one thing this side does not own.
   */
  select(slot: number): void {
    this.queuedSlot = slot;
  }

  /**
   * Ask the room to end the live round — see `QueuedActions.endRound`.
   *
   * **A LATCH RATHER THAN A COUNT, which is where it parts company with `press`.** A split is a
   * quantity, so three presses are three doublings; this is a *request about the room*, and the
   * room's answer to a second one is the same as its answer to the first. So a player mashing it
   * while the frames go out sends one `true` per frame at worst rather than owing five requests
   * to a round that ended after the first.
   */
  endRound(): void {
    this.queuedEndRound = true;
  }

  /** Answer the room's vote. An option **id**, never a label and never an index. */
  vote(optionId: string): void {
    this.heldVote = optionId;
  }

  /**
   * The answer to put on this frame, or null — read straight rather than through `take()`,
   * because this is the one queued value a frame does not spend. See `heldVote`.
   */
  get voteAnswer(): string | null {
    return this.heldVote;
  }

  /** The vote is over (or a new one has opened): stop re-stating the answer. */
  clearVote(): void {
    this.heldVote = null;
  }

  /**
   * The slot a frame would carry if one went out now, or null.
   *
   * Read by the cycle control, which is the one caller that needs a *base* to count from: two
   * presses inside one tick have to be two steps rather than the same step twice, and the only
   * record of the first one is this. Everything else names a slot outright and needs nothing.
   */
  get selectedSlot(): number | null {
    return this.queuedSlot;
  }

  /** Whether an input frame has to go out this tick even if the heading has not moved. */
  get pending(): boolean {
    // The vote answer is in here for the same reason as the other two and with one extra
    // consequence: because a frame does not spend it, this keeps a frame going out every tick for
    // as long as the answer is held, which IS the re-send. See `heldVote`.
    if (
      this.queuedSaying !== null ||
      this.queuedSlot !== null ||
      this.heldVote !== null ||
      this.queuedEndRound
    ) {
      return true;
    }
    for (const action of EDGE_ACTIONS) {
      if (this.owed[action] > 0) return true;
    }
    return false;
  }

  /** How many presses of one action are still owed. Exposed for tests and for nothing else. */
  depth(action: EdgeAction): number {
    return this.owed[action];
  }

  /** Take one of each owed action — the most a single frame is allowed to carry. */
  take(): QueuedActions {
    const actions: QueuedActions = {};
    // First on the frame, because the simulation spends a switch before a firing and a frame
    // carrying both means those two presses in that order — see `RoomSim.stepPowerUpSwitch`.
    if (this.queuedSlot !== null) {
      actions.selectSlot = this.queuedSlot;
      this.queuedSlot = null;
    }
    if (this.owed.split > 0) {
      this.owed.split -= 1;
      actions.split = true;
    }
    if (this.owed.eject > 0) {
      this.owed.eject -= 1;
      actions.eject = true;
    }
    if (this.owed.powerUp > 0) {
      this.owed.powerUp -= 1;
      actions.usePowerUp = true;
    }
    if (this.queuedSaying !== null) {
      actions.saying = this.queuedSaying;
      this.queuedSaying = null;
    }
    if (this.queuedEndRound) {
      actions.endRound = true;
      this.queuedEndRound = false;
    }
    return actions;
  }

  /** A new life owes nothing the last one asked for. */
  clear(): void {
    for (const action of EDGE_ACTIONS) this.owed[action] = 0;
    this.queuedSaying = null;
    this.queuedSlot = null;
    this.heldVote = null;
    this.queuedEndRound = false;
  }
}

/**
 * How long a control has to stay down before it starts repeating, and how often it does then.
 *
 * The delay is what makes a tap one press: a button held for three animation frames must not
 * become three splits. The interval is TICK_MS because that is the fastest the wire can carry
 * them anyway — repeating faster would only fill the queue.
 */
export const HOLD_REPEAT_DELAY_MS = 260;
export const HOLD_REPEAT_INTERVAL_MS = TICK_MS;
/** Presses one `repeat` may owe. A backgrounded tab hands back a delta of seconds; ignore it. */
const HOLD_REPEAT_MAX_BURST = 2;

/**
 * The repeat clock of one held control — and *only* the repeat.
 *
 * The first press is the caller's, taken from the event that started the hold (`keydown`,
 * `pointerdown`), and that split is the whole design. A control polled for its first press can
 * miss one entirely: a key pressed and released between two animation frames is never observed
 * down, and a tap that fast is a tap a player made. An event cannot miss it. What an event
 * *cannot* do is tell you a control is still down 300 ms later, which is what `repeat` is for.
 *
 * Deliberately a sampler after that: the caller says the control is still down and is told how
 * many presses that owes, so the same class serves Phaser's per-frame `key.isDown` and a React
 * button's interval — and the repeat rate is this game's rather than the operating system's
 * key-repeat setting.
 */
export class HoldRepeat {
  private nextRepeatMs: number | null = null;

  /** The control went down. The caller fires its own first press; this starts the clock. */
  begin(nowMs: number): void {
    this.nextRepeatMs = nowMs + HOLD_REPEAT_DELAY_MS;
  }

  /** Presses owed by a control that is still down. Zero until the delay has passed. */
  repeat(nowMs: number): number {
    if (this.nextRepeatMs === null) return 0;
    let fired = 0;
    while (nowMs >= this.nextRepeatMs && fired < HOLD_REPEAT_MAX_BURST) {
      this.nextRepeatMs += HOLD_REPEAT_INTERVAL_MS;
      fired += 1;
    }
    // Whatever the gap was, the next repeat is one interval from now — a tab that came back from
    // the background owes the presses it would have owed had it been watched, and no more.
    if (nowMs >= this.nextRepeatMs) this.nextRepeatMs = nowMs + HOLD_REPEAT_INTERVAL_MS;
    return fired;
  }

  /** The control came up (or the scene is going away). `repeat` owes nothing until `begin`. */
  release(): void {
    this.nextRepeatMs = null;
  }
}

/** How close together two downs have to be, in time and in screen pixels, to be one gesture. */
export const DOUBLE_TAP_MS = 320;
export const DOUBLE_TAP_SLOP_PX = 48;

/**
 * Two downs, one gesture.
 *
 * Consumes its pair, so four taps are two splits and three are one: the alternative — a sliding
 * window — turns a mashed button into a split on every tap after the first, which is a different
 * control from the one the player thinks they are using.
 */
export class DoubleTap {
  private lastMs = Number.NEGATIVE_INFINITY;
  private lastX = 0;
  private lastY = 0;

  /** True when this down completes a double tap. Coordinates are screen pixels. */
  tap(x: number, y: number, nowMs: number): boolean {
    const paired =
      nowMs - this.lastMs <= DOUBLE_TAP_MS &&
      Math.hypot(x - this.lastX, y - this.lastY) <= DOUBLE_TAP_SLOP_PX;
    if (paired) {
      this.reset();
      return true;
    }
    this.lastMs = nowMs;
    this.lastX = x;
    this.lastY = y;
    return false;
  }

  reset(): void {
    this.lastMs = Number.NEGATIVE_INFINITY;
  }
}

/**
 * Slack around a cell, as a fraction of its radius, for "did that tap land on me".
 *
 * A finger is wider than the point the browser reports and a fresh player is 18 world units
 * across; without the slack the gesture would only be reachable by the players who least need it.
 */
export const OWN_BLOB_TAP_SLOP_RATIO = 0.4;

/**
 * Whether a world-space point is on our own cluster, which is what a touch double-tap is asked
 * to land on. Tested against where the cells are *drawn* — prediction has usually run ahead of
 * the last authoritative frame, and a hit test against the truth would miss the blob the player
 * can actually see.
 */
export function withinOwnCluster(
  worldX: number,
  worldY: number,
  cells: readonly MassPoint[],
  offsetX: number,
  offsetY: number,
): boolean {
  for (const cell of cells) {
    const radius = massToRadius(cell.mass) * (1 + OWN_BLOB_TAP_SLOP_RATIO);
    if (Math.hypot(worldX - (cell.x + offsetX), worldY - (cell.y + offsetY)) <= radius) {
      return true;
    }
  }
  return false;
}

/**
 * The slot a cycle key moves to: one along, wrapping, or null when there is nowhere to go.
 *
 * Pure and here rather than in the connection because it is the only arithmetic the switch
 * control does, and because "a cycle with one slot in it is not a switch" is a rule worth pinning
 * rather than discovering. An inventory of one has exactly one thing to point at, and asking the
 * server to re-select the slot it is already on is free but pointless — and on a *timed* slot it
 * is the one press that must never be mistaken for a switch away, which the simulation already
 * guarantees and this refuses to lean on.
 */
export function nextSlotIndex(current: number, count: number): number | null {
  if (count <= 1) return null;
  // A `current` from outside the list — a frame that shrank the inventory under a queued press —
  // starts the cycle at the top rather than at a negative index.
  if (!Number.isInteger(current) || current < 0 || current >= count) return 0;
  return (current + 1) % count;
}

/**
 * Every key the arena binds, in one table, so that "no two controls claim the same key" is a
 * thing a test pins rather than a thing somebody remembers.
 *
 * It used to be a dozen string literals inside `ArenaScene.create`, which is exactly the place a
 * collision is invisible: Phaser will happily hand two controls the same `Key`, and what a player
 * gets is one press doing two things with nothing failing anywhere. The sprint is what forced the
 * table — its binding had to be checked against every existing one rather than against memory —
 * and `actions.test.ts` is where that check now lives for good.
 *
 * The names are Phaser's own (`Phaser.Input.Keyboard.KeyCodes`), because `addKey` is the one
 * consumer and a second spelling would be a second thing to keep in step.
 */
export const ARENA_KEY_BINDINGS = {
  /** No 'W': it ejects, which is why the up stroke of the movement cross is arrows-only. */
  up: ['UP'],
  down: ['S', 'DOWN'],
  left: ['A', 'LEFT'],
  right: ['D', 'RIGHT'],
  split: ['SPACE'],
  eject: ['W'],
  /** Next to the key that already ejects, and nowhere near the movement cross. */
  powerUp: ['Q'],
  /** Walks along the inventory. See `slotDirect` for the other half of that control. */
  slotCycle: ['E'],
  /**
   * The keys that name an inventory slot outright, in slot order.
   *
   * Sliced to POWER_UP_INVENTORY_SIZE where they are bound, so widening the inventory is one more
   * entry here and nothing else — and a build whose inventory is bigger than this list simply
   * binds fewer keys rather than crashing, because the cycle key reaches every slot on its own.
   * All four are reserved by this table whether or not they are bound, which is what stops a
   * later control quietly claiming `FOUR`.
   */
  slotDirect: ['ONE', 'TWO', 'THREE', 'FOUR'],
  /**
   * **SPRINT, AND THE BINDING IS THE SHARPEST DECISION IN THE CONTROL.**
   *
   * Every other key in this table was already spoken for: the movement cross is the arrows plus
   * A/S/D, SPACE splits, W ejects, Q fires and E plus the number row work the inventory. SHIFT
   * collides with none of them, it is the genre's own convention for "run" — a player arrives
   * already knowing it — and it is the one key on a keyboard that a hand resting on the movement
   * cross can hold *with the same hand*, which matters more here than for any other control in
   * this game because a sprint is held for seconds rather than tapped.
   *
   * It is also a modifier, which is a feature rather than a hazard: nothing in the arena is bound
   * to SHIFT + anything, so a player holding it keeps every other control they had.
   */
  sprint: ['SHIFT'],
} as const satisfies Record<string, readonly string[]>;

/** What a sprint key is called, for the one place it is bound. */
export const SPRINT_KEY_NAMES = ARENA_KEY_BINDINGS.sprint;

/**
 * Whether the on-screen sprint button is being held down, from React to the render loop.
 *
 * **A LEVEL, NOT AN EDGE, AND DELIBERATELY NOT AN `ActionQueue` OR A `HoldRepeat`.** There is
 * nothing here to count and nothing to spend: what the wire carries is the answer to "is the
 * control down on this frame" (`clientInputSchema.sprint`), so a queue would deliver a press the
 * player has already stopped asking for and a repeat clock would turn one continuous instruction
 * into twenty identical ones a second. See the section at the top of this file.
 *
 * **It is the BUTTON's half and never the keyboard's.** A module singleton for `hudSettings`' and
 * `JoystickStore`'s stated reason — two readers that never meet, a React control writing and the
 * Phaser scene reading, and handing it through props would mean plumbing a React value into the
 * render loop. The two halves are ORed in `ArenaScene` rather than here, exactly as the stick and
 * the pointer are folded into one heading there: the scene is where every input this game has
 * converges, and `GameConnection` is handed one answer rather than two sources it would have to
 * arbitrate between. A connection that took `setSprint(false)` from the keyboard on every frame
 * would cancel the button on every frame.
 *
 * **A STRANDED SPRINT IS THE FAILURE THIS IS DESIGNED AGAINST**, which is why `release` is
 * idempotent and is reachable from every way a press can end — the pointer coming up, the browser
 * cancelling it, the capture being lost, and the control unmounting. There is no "toggle": a
 * level that could be left on by a gesture nobody finished is a player sprinting until their bar
 * is empty for a button they let go of.
 */
export class SprintControl {
  private down = false;

  /** Whether the on-screen control is down. Read once per frame by the scene. */
  get held(): boolean {
    return this.down;
  }

  press(): void {
    this.down = true;
  }

  release(): void {
    this.down = false;
  }
}

/** The one every reader shares — see `SprintControl` for why this is a singleton. */
export const sprintControl = new SprintControl();
